// Marketing copy for the plan cards on /upgrade, transcribed from the public
// pricing page. Deliberately not derived from the entitlements catalog.

export const TIER_ORDER = ['core', 'pro', 'ultra'] as const;
export type TierKey = typeof TIER_ORDER[number];

/** CLOUD-684: Upgrade shows to a billing owner below the top plan only. */
export function canUpgradeIn(workspace: { isBillingOwner: boolean; subscriptionPlan: TierKey } | null | undefined) {
  return Boolean(workspace?.isBillingOwner) && workspace?.subscriptionPlan !== 'ultra';
}

export type Plan = {
  id: TierKey;
  tier: string;
  price: string;
  priceUnit?: string;
  description: string;
  features: string[];
  current?: boolean;
};

export const PLAN_META: Record<TierKey, Omit<Plan, 'features'>> = {
  core: {
    id: 'core',
    tier: 'Core',
    price: '$0.00',
    priceUnit: 'per device / month',
    description: 'Everything you need to get your Ari devices set up and running.',
  },
  pro: {
    id: 'pro',
    tier: 'Pro',
    price: '$9.90',
    priceUnit: 'per device / month',
    description: 'Built for people running Ari as part of their day-to-day.',
  },
  ultra: {
    id: 'ultra',
    tier: 'Ultra',
    price: '$19.90',
    priceUnit: 'per device / month',
    description: 'Enterprise-grade control for large, security-first deployments.',
  },
};

const PLAN_CARD_FEATURES: Record<TierKey, string[]> = {
  core: [
    'On device voice control',
    'Ari usage allowance',
    'Custom skills and connectors',
    '10 GB photos and videos, 10 GB documents',
    'Basic Collaborate features',
  ],
  pro: [
    'On device voice control',
    '3x Ari usage allowance',
    'Custom skills and connectors',
    '100 GB photos and videos, 100 GB documents',
    'Advanced Collaborate features',
    'Up to 5 device groups',
  ],
  ultra: [
    'On device voice control',
    '5x Ari usage allowance',
    'Custom skills and connectors',
    '500 GB photos and videos, 500 GB documents',
    'Professional Collaborate features',
    'Unlimited device groups',
    'Kiosk Mode',
  ],
};

/** Cards in display order. `current` is set by the view from the user's tier. */
export const UPGRADE_PLANS: Plan[] = TIER_ORDER.map((tier) => ({
  ...PLAN_META[tier],
  features: PLAN_CARD_FEATURES[tier],
}));
